import React, { useEffect, useState } from "react";
import { Plus, Minus, Trash2, Clock, Users, ChevronDown, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import {
  listSalonSessions,
  createSessionTemplate,
  deleteSessionTemplate,
  listSessionInstances,
  adjustInstanceCapacity,
} from "../../lib/api";

const CATEGORIES = ["Sauna", "Contrast Therapy", "Bathhouse", "Float Tank", "Infrared Sauna", "Massage", "Hair", "Skin", "Brows + Lashes", "Nails", "Body"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function SessionManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const data = await listSalonSessions();
      setTemplates(data);
    } catch (_e) { /* non-fatal */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onCreated = (t) => {
    setTemplates((prev) => [...prev, t]);
    setShowCreate(false);
    toast.success(`Session "${t.service_name}" created with instances.`);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this session template and all future instances?")) return;
    try {
      await deleteSessionTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Session deleted.");
    } catch (_e) {
      toast.error("Failed to delete.");
    }
  };

  if (loading) return <div className="animate-pulse h-20 bg-sand/30 rounded-2xl" />;

  return (
    <div data-testid="session-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-xl text-ink">Sessions</h2>
          <p className="text-xs text-ink/45 mt-0.5">Recurring sessions with capacity (wellness, sauna, classes)</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="inline-flex items-center gap-1.5 rounded-full bg-espresso text-bone px-4 py-2 text-[13px] hover:bg-ink transition-colors"
          data-testid="session-create-btn"
        >
          <Plus size={13} strokeWidth={1.5} /> New session
        </button>
      </div>

      {showCreate && <CreateSessionForm onCreated={onCreated} onCancel={() => setShowCreate(false)} />}

      {templates.length === 0 && !showCreate && (
        <div className="bg-bone border border-dashed border-sand rounded-2xl p-8 text-center text-sm text-ink/55">
          Nothing here yet. Once you start filling gaps, your history will show here.
        </div>
      )}

      <div className="space-y-3 mt-3">
        {templates.map((t) => (
          <SessionTemplateCard key={t.id} template={t} onDelete={() => onDelete(t.id)} />
        ))}
      </div>
    </div>
  );
}


function CreateSessionForm({ onCreated, onCancel }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Sauna");
  const [duration, setDuration] = useState(45);
  const [price, setPrice] = useState(65);
  const [capacity, setCapacity] = useState(2);
  const [selectedDays, setSelectedDays] = useState(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [times, setTimes] = useState("09:00, 10:00, 11:00, 12:00, 13:00, 14:00");
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => setSelectedDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);

    const timeList = times.split(",").map((t) => t.trim()).filter(Boolean);
    const schedule = selectedDays.map((d) => ({ day_of_week: d, times: timeList }));

    try {
      const result = await createSessionTemplate({
        service_name: name.trim(),
        category,
        duration_min: duration,
        price,
        capacity,
        recurrence: "custom",
        schedule,
        active: true,
      });
      onCreated(result);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create session.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="bg-bone border border-sand/70 rounded-2xl p-5 md:p-6 mb-4" data-testid="session-create-form">
      <p className="font-serif text-base text-ink mb-4">New recurring session</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Session name</span>
          <input
            type="text" required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Infrared Sauna · 45min"
            className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
            data-testid="session-name-input"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Category</span>
          <select
            value={category} onChange={(e) => setCategory(e.target.value)}
            className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px] bg-bone"
            data-testid="session-category-select"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-4">
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Duration (min)</span>
          <input
            type="number" min={5} max={480} value={duration} onChange={(e) => setDuration(+e.target.value)}
            className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
            data-testid="session-duration-input"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Price ($)</span>
          <input
            type="number" min={0} step={0.01} value={price} onChange={(e) => setPrice(+e.target.value)}
            className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
            data-testid="session-price-input"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Spots per session</span>
          <input
            type="number" min={1} max={100} value={capacity} onChange={(e) => setCapacity(+e.target.value)}
            className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
            data-testid="session-capacity-input"
          />
        </label>
      </div>

      {/* Days */}
      <div className="mt-4">
        <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-2">Days</span>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button
              key={d} type="button" onClick={() => toggleDay(d)}
              className={`rounded-full px-3 py-1.5 text-[13px] border transition-colors ${
                selectedDays.includes(d) ? "bg-espresso text-bone border-espresso" : "bg-bone text-ink/60 border-sand hover:border-espresso"
              }`}
              data-testid={`session-day-${d.toLowerCase()}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Times */}
      <label className="block mt-4">
        <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Times (comma-separated)</span>
        <input
          type="text" value={times} onChange={(e) => setTimes(e.target.value)}
          placeholder="09:00, 10:00, 11:00, 12:00"
          className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
          data-testid="session-times-input"
        />
        <p className="text-[11px] text-ink/40 mt-1">24h format. Sessions generated for the next 7 days.</p>
      </label>

      <div className="flex gap-3 mt-5">
        <button
          type="submit" disabled={saving}
          className={`inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-5 py-2.5 text-sm transition-colors ${saving ? "opacity-60" : "hover:bg-ink"}`}
          data-testid="session-create-submit"
        >
          {saving ? "Creating…" : "Create session"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-sand px-5 py-2.5 text-sm text-ink/60 hover:text-ink transition-colors" data-testid="session-create-cancel">
          Cancel
        </button>
      </div>
    </form>
  );
}


function SessionTemplateCard({ template, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);

  const toggleExpand = async () => {
    if (!expanded && instances.length === 0) {
      setLoadingInstances(true);
      try {
        const data = await listSessionInstances(template.id);
        setInstances(data);
      } catch (_e) { /* non-fatal */ }
      setLoadingInstances(false);
    }
    setExpanded(!expanded);
  };

  const onAdjust = async (instId, delta) => {
    try {
      const updated = await adjustInstanceCapacity(instId, delta);
      setInstances((prev) => prev.map((inst) =>
        inst.id === instId ? { ...inst, total_capacity: updated.total_capacity, spots_left: updated.spots_left } : inst
      ));
    } catch (err) {
      toast.error("Failed to adjust capacity.");
    }
  };

  const schedDays = (template.schedule || []).map((s) => s.day_of_week).join(", ");
  const schedTimes = (template.schedule?.[0]?.times || []).length;

  return (
    <div className="bg-bone border border-sand/70 rounded-2xl overflow-hidden" data-testid={`session-template-${template.id}`}>
      {/* Header row */}
      <div className="flex items-center gap-3 px-5 py-4 cursor-pointer" onClick={toggleExpand} data-testid={`session-expand-${template.id}`}>
        <span className="text-mocha shrink-0">
          {expanded ? <ChevronDown size={16} strokeWidth={1.5} /> : <ChevronRight size={16} strokeWidth={1.5} />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-base text-ink truncate">{template.service_name}</p>
          <p className="text-xs text-ink/50 mt-0.5">
            {template.category} · {template.duration_min}min · ${template.price} · {template.capacity} spots
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs text-ink/45 shrink-0">
          <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {schedDays}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {schedTimes} slots/day</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="ml-2 text-ink/30 hover:text-red-600 transition-colors shrink-0"
          data-testid={`session-delete-${template.id}`}
        >
          <Trash2 size={14} strokeWidth={1.5} />
        </button>
      </div>

      {/* Expanded: instances */}
      {expanded && (
        <div className="border-t border-sand/60 px-5 py-4">
          {loadingInstances ? (
            <div className="animate-pulse h-10 bg-sand/20 rounded" />
          ) : instances.length === 0 ? (
            <p className="text-sm text-ink/40 italic">No upcoming instances.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 text-[10px] tracking-[0.18em] uppercase text-mocha/60 pb-1">
                <span className="col-span-3">Date</span>
                <span className="col-span-2">Time</span>
                <span className="col-span-2">Capacity</span>
                <span className="col-span-2">Booked</span>
                <span className="col-span-2">Left</span>
                <span className="col-span-1"></span>
              </div>
              {instances.map((inst) => (
                <div key={inst.id} className="grid grid-cols-12 items-center text-sm py-1.5" data-testid={`session-instance-${inst.id}`}>
                  <span className="col-span-3 text-ink/70">{inst.date_str}</span>
                  <span className="col-span-2 text-ink/70">{inst.time}</span>
                  <span className="col-span-2 font-serif text-ink">{inst.total_capacity}</span>
                  <span className="col-span-2 font-serif text-mocha">{inst.booked_spots}</span>
                  <span className={`col-span-2 font-serif ${inst.spots_left === 0 ? "text-red-600" : "text-espresso"}`}>
                    {inst.spots_left}
                  </span>
                  <span className="col-span-1 flex items-center gap-1">
                    <button
                      onClick={() => onAdjust(inst.id, -1)}
                      className="w-6 h-6 rounded border border-sand flex items-center justify-center text-ink/50 hover:text-red-600 hover:border-red-300 transition-colors"
                      data-testid={`instance-minus-${inst.id}`}
                    >
                      <Minus size={11} strokeWidth={2} />
                    </button>
                    <button
                      onClick={() => onAdjust(inst.id, 1)}
                      className="w-6 h-6 rounded border border-sand flex items-center justify-center text-ink/50 hover:text-emerald-600 hover:border-emerald-300 transition-colors"
                      data-testid={`instance-plus-${inst.id}`}
                    >
                      <Plus size={11} strokeWidth={2} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
